import React, { useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import { longDateString } from "../../utils";
import "../../starek.css";

// Work hours are seconds from midnight, the same encoding the Shifts screen uses.
const formatWorkTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

// Hours come from the linked shift, so a run always shows that shift's current times.
const workHours = (row) =>
    row.shift
        ? `${formatWorkTime(row.shift.start_time)} - ${formatWorkTime(row.shift.end_time)}`
        : "";

function JobSchedulesListingView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    toggleCleared,
    noDataComponent,
    onRowClicked,
}) {
    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                String(item.name || "")
                    .toLowerCase()
                    .includes(q) ||
                String(item.description || "")
                    .toLowerCase()
                    .includes(q) ||
                String(item.part?.name || "")
                    .toLowerCase()
                    .includes(q) ||
                (item.operations || []).some((op) =>
                    String(op.operation?.name || "")
                        .toLowerCase()
                        .includes(q),
                ) ||
                String(item.device_id || "")
                    .toLowerCase()
                    .includes(q) ||
                workHours(item).toLowerCase().includes(q)
        );
    }, [items, filterText]);

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            height: "50px",
            grow: 1.2,
        },
        {
            name: "Part",
            selector: (r) => r.part?.name || "-",
            sortable: true,
            height: "50px",
            width: "150px",
        },
        {
            name: "Machine",
            selector: (r) => r.device_id || "-",
            sortable: true,
            height: "50px",
            width: "120px",
        },
        {
            name: "Progress",
            selector: (r) => (r.operations || []).length,
            sortable: true,
            height: "50px",
            width: "170px",
            cell: (r) => {
                const ops = r.operations || [];
                if (ops.length === 0) {
                    return <span className="text-muted">-</span>;
                }
                // Status 2 is Completed; see JOB_SCHEDULE_OPERATION_STATUSES.
                const done = ops.filter((o) => o.status === 2).length;
                const complete = done === ops.length;
                return (
                    <div style={{ fontSize: "0.85rem" }}>
                        <span
                            className={`badge bg-${complete ? "success" : done ? "primary" : "secondary"} me-1`}
                        >
                            {done} of {ops.length}
                        </span>
                        operations
                    </div>
                );
            },
        },
        {
            name: "Cycles",
            selector: (r) => r.completed_cycles ?? 0,
            sortable: true,
            height: "50px",
            width: "100px",
            center: true,
            cell: (r) => (
                <span className="badge bg-info">{r.completed_cycles ?? 0}</span>
            ),
        },
        {
            name: "Shift",
            selector: (r) => r.shift?.name ?? "",
            sortable: true,
            height: "50px",
            width: "160px",
            cell: (r) => {
                const hours = workHours(r);
                return (
                    <div
                        style={{ fontSize: "0.85rem" }}
                        title={hours || "Runs the full range"}
                    >
                        {r.shift ? (
                            <>
                                <div>{r.shift.name}</div>
                                <div
                                    style={{
                                        fontSize: "0.75rem",
                                        color: "#6c757d",
                                    }}
                                >
                                    {hours}
                                </div>
                            </>
                        ) : (
                            "Full range"
                        )}
                    </div>
                );
            },
        },
        {
            name: "Start Time",
            selector: (r) =>
                r.start_time ? moment(r.start_time).format("lll") : "-",
            sortable: true,
            height: "50px",
            width: "180px",
        },
        {
            name: "End Time",
            selector: (r) =>
                r.end_time ? moment(r.end_time).format("lll") : "-",
            sortable: true,
            height: "50px",
            grow: 1,
        },
        {
            name: "Activity",
            selector: (r) => r.updated_at,
            sortable: true,
            height: "50px",
            width: "250px",
            cell: (r) => {
                const tooltipText = `Created by ${
                    r.created_by?.name || "Unknown"
                } at ${
                    r.created_at ? longDateString(r.created_at) : "-"
                }\nModified by ${r.updated_by?.name || "Unknown"} at ${
                    r.updated_at ? longDateString(r.updated_at) : "-"
                }`;
                return (
                    <div style={{ fontSize: "0.85rem" }} title={tooltipText}>
                        {r.updated_by?.name || "Unknown"} at{" "}
                        {r.updated_at ? longDateString(r.updated_at) : "-"}
                    </div>
                );
            },
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={filteredItems}
            selectableRows
            onSelectedRowsChange={onSelectedRowsChange}
            clearSelectedRows={toggleCleared}
            highlightOnHover
            striped
            pointerOnHover
            onRowClicked={(row) => onRowClicked?.(row)}
            responsive
            pagination
            dense
            selectableRowsHighlight
            selectableRowsNoSelectAll={false}
            noDataComponent={noDataComponent}
        />
    );
}

export default JobSchedulesListingView;
